import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HomeIcon from '@mui/icons-material/Home';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { Alert, Box, Button, Snackbar, Stack, Typography } from '@mui/material';
import OrdersDialog from '../components/OrdersDialog';
import { getLastOrderToken } from '../services/pedidoService';

const CHECKOUT_RESUME_KEY = 'better_checkout_resume';

const STATUS_CONFIG = {
  sucesso: {
    title: 'Pagamento aprovado!',
    description:
      'Recebemos a confirmação do Mercado Pago. Seu pedido está sendo processado e você pode acompanhar o status quando quiser.',
    icon: <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main' }} />,
    severity: 'success' as const,
  },
  pendente: {
    title: 'Pagamento em análise',
    description:
      'O Mercado Pago está analisando o pagamento. Assim que houver uma atualização, avisaremos por aqui e pelo WhatsApp.',
    icon: <HourglassEmptyIcon sx={{ fontSize: 64, color: 'warning.main' }} />,
    severity: 'warning' as const,
  },
  erro: {
    title: 'Pagamento não aprovado',
    description:
      'Não conseguimos aprovar o pagamento. Você pode tentar novamente ou escolher outro método diretamente com a loja.',
    icon: <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main' }} />,
    severity: 'error' as const,
  },
};

type StatusKey = keyof typeof STATUS_CONFIG;

export default function PaymentResultPage() {
  const navigate = useNavigate();
  const { status } = useParams<{ status: string }>();
  const [searchParams] = useSearchParams();
  const [isOrdersDialogOpen, setIsOrdersDialogOpen] = useState(false);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [hasProcessedParams, setHasProcessedParams] = useState(false);

  const normalizedStatus = (status ?? '').toLowerCase() as StatusKey;

  // Processar parâmetros de retorno do Mercado Pago
  useEffect(() => {
    if (hasProcessedParams) {
      return undefined;
    }

    let redirectTimeout: ReturnType<typeof setTimeout> | undefined;

    const processPaymentResult = async () => {
      const hashFragment = window.location.hash.startsWith('#')
        ? window.location.hash.slice(1)
        : window.location.hash;
      const combinedQuery = [searchParams.toString(), hashFragment]
        .filter(Boolean)
        .join('&');
      const params = new URLSearchParams(combinedQuery);

      const paymentStatusRaw = params.get('status') ?? params.get('collection_status');
      const paymentId = params.get('payment_id') ?? params.get('collection_id') ?? params.get('id');

      if (!paymentStatusRaw && !paymentId) {
        setHasProcessedParams(true);
        return;
      }

      try {
        if (paymentId) {
          const API_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:3000/api';

          const response = await fetch(`${API_URL}/payments/mercadopago/webhook`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              topic: 'payment',
              type: 'payment',
              data: {
                id: paymentId,
              },
              status: paymentStatusRaw ?? undefined,
            }),
          });

          if (!response.ok) {
            const errorText = await response.text();
            setNotification({
              open: true,
              message:
                'Não foi possível atualizar o status do pagamento. Atualize a página ou tente novamente em alguns instantes.',
              severity: 'error',
            });
            throw new Error(`Webhook responded with ${response.status} ${response.statusText}: ${errorText}`);
          }
        }

        const normalizedPaymentStatus = (paymentStatusRaw ?? '').toLowerCase();

        if (normalizedPaymentStatus === 'approved') {
          localStorage.removeItem('better_cart');
          localStorage.removeItem('better_checkout_info');
          localStorage.removeItem('better_payment_error');
          localStorage.removeItem(CHECKOUT_RESUME_KEY);
        } else if (['rejected', 'cancelled', 'failure', 'expired'].includes(normalizedPaymentStatus)) {
          const resumePayload = {
            timestamp: Date.now(),
            message: 'Pagamento não foi aprovado. Revise os dados ou escolha outra forma de pagamento.',
            resetPaymentMode: true,
            reopenCheckout: true,
          } as const;

          try {
            localStorage.setItem(CHECKOUT_RESUME_KEY, JSON.stringify(resumePayload));
            localStorage.setItem(
              'better_payment_error',
              JSON.stringify({ message: resumePayload.message, timestamp: resumePayload.timestamp }),
            );
          } catch (storageError) {
            console.error('Erro ao preparar retomada do checkout:', storageError);
          }

          redirectTimeout = setTimeout(() => {
            navigate('/', { replace: true });
          }, 2500);
        }
      } catch (error) {
        console.error('Erro ao processar retorno do pagamento:', error);
        setNotification({
          open: true,
          message:
            'Ocorreu um erro ao processar o retorno do pagamento. Verifique sua conexão e tente novamente.',
          severity: 'error',
        });
      } finally {
        setHasProcessedParams(true);
      }
    };

    const hasAnyParam = searchParams.size > 0 || !!window.location.hash;

    if (hasAnyParam && !hasProcessedParams) {
      processPaymentResult();
    } else if (!hasAnyParam) {
      setHasProcessedParams(true);
    }

    return () => {
      if (redirectTimeout) {
        clearTimeout(redirectTimeout);
      }
    };
  }, [searchParams, hasProcessedParams, navigate]);

  const config = useMemo(() => {
    if (STATUS_CONFIG[normalizedStatus]) {
      return STATUS_CONFIG[normalizedStatus];
    }
    return {
      title: 'Status desconhecido',
      description:
        'Não encontramos informações sobre este pagamento. Tente acompanhar o pedido diretamente ou volte ao cardápio.',
      icon: <HourglassEmptyIcon sx={{ fontSize: 64, color: 'text.secondary' }} />,
      severity: 'info' as const,
    };
  }, [normalizedStatus]);

  const hasRecentOrder = useMemo(() => !!getLastOrderToken(), []);

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 6, md: 10 },
        px: { xs: 2, md: 0 },
      }}
    >
      <Stack spacing={4} maxWidth={520} width="100%" alignItems="center" textAlign="center">
        {config.icon}

        <Stack spacing={1} width="100%">
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            {config.title}
          </Typography>

          <Typography variant="body1" color="text.secondary">
            {config.description}
          </Typography>

          {normalizedStatus === 'erro' && (
            <Alert severity="warning">
              Caso o valor tenha sido debitado, não se preocupe. O Mercado Pago fará a reversão automaticamente em até alguns
              minutos.
            </Alert>
          )}
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} width="100%" justifyContent="center">
          <Button
            variant="contained"
            color="primary"
            startIcon={<HomeIcon />}
            onClick={() => navigate('/')}
            fullWidth
            sx={{ maxWidth: { sm: 220 } }}
          >
            {normalizedStatus === 'erro' ? 'Tentar novamente' : 'Voltar ao cardápio'}
          </Button>

          <Button
            variant="outlined"
            color="primary"
            startIcon={<LocalShippingIcon />}
            onClick={() => setIsOrdersDialogOpen(true)}
            fullWidth
            sx={{ maxWidth: { sm: 220 } }}
            disabled={!hasRecentOrder}
          >
            Acompanhar pedido
          </Button>
        </Stack>
      </Stack>

      <OrdersDialog open={isOrdersDialogOpen} onClose={() => setIsOrdersDialogOpen(false)} />

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

    </Box>
  );
}
